import { inject, Injectable } from '@angular/core';
import { IFindRequestParams, IMetaCallRequest, IMetaCallResponse, IResponseMeta, RequestService } from '@df/api/request/request.service';
import { IModelData, Model, TLoadParams } from '@df/catalog/model';
import { App } from '@df/core/app';
import { Events } from '@df/core/service/event';
import { TimeoutService } from '@df/ng/timeout.service';
import compact from 'lodash-es/compact';
import each from 'lodash-es/each';
import matches from 'lodash-es/matches';
import { Subject } from 'rxjs';

export interface IInstance<T extends Model<IModelData, I>, I extends number | string = number> {
    type: string;
    routable: boolean;
    new (id: I): T;
}

// tslint:disable:no-empty-interface
export interface IResources {}
// tslint:enable:no-empty-interface

export interface IResourceIndex {
    [attributeValue: number | string]: { [modelId: number]: boolean };
}

export const ADD_RESOURCE_EVENT = 'app.resource.created';

declare module '@df/core/app' {
    interface App {
        resources: IResources;
    }
}

@Injectable()
export abstract class Resource<T extends Model<IModelData, I>, I extends number | string = string | number> {
    static instance: IInstance<any, any>;
    protected instance!: IInstance<any, any>;

    abstract readonly invalidatesUniversal: boolean;
    abstract readonly invalidatesPrerender: boolean;

    protected indexes: {
        [attributeCode: string]: {
            [attributeValue: number | string]: { [modelId: number]: boolean };
        };
    } = {};

    /**
     * Existing models
     */
    protected assets: { [id: string]: T } = {};
    /**
     * Existing models
     * from other wstores
     */
    protected externalAssets: { [store: number]: { [id: string]: T } } = {};

    private onCreateSubject = new Subject<T>();

    /**
     * Request service
     */

    protected requestService = inject(RequestService);
    protected timeoutService = inject(TimeoutService);
    protected events = inject(Events);
    protected app = inject(App);
    private get multisiteService() {
        return this.app.multisite;
    }
    private get logger() {
        return this.app.logger;
    }
    private get cacheService() {
        return this.app.cache;
    }

    get type(): string {
        return this.instance.type;
    }

    constructor() {
        if (!this.app.resources) {
            this.app.resources = {} as IResources;
        }
        this.instance = this.constructor['instance'];
        if (!this.instance) {
            throw new Error('Resource instance not defined');
        }
        const type = this.instance.type;
        if (this.app.resources[type]) {
            console.error('Double registration of service: ', type, this);
        }
        this.app.resources[type] = this as any as Resource<Model<IModelData, string | number>, string | number>;
        this.events.broadcast(ADD_RESOURCE_EVENT, this);
        this.requestService.subscribe(result => result && this.requestServiceObserver(result));
    }

    /**
     * Register observer
     * Observer is called on model instance creation
     * @param {(model: T) => void} plugin
     */
    addPlugin(plugin: (model: T) => void) {
        // we need to add plugin to all models already crated (for example added from universal)
        const models = this.getAll();
        models.forEach(plugin);

        this.onCreateSubject.subscribe(plugin);
    }

    /**
     * Listener to data coming from BE
     * If it matches resource type then model will be created/updated
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {IResponseMeta} meta
     */
    protected requestServiceObserver(meta: IResponseMeta): void {
        if (meta.result && meta.result.catalog && Array.isArray(meta.result.catalog)) {
            const type = this.instance.type;
            meta.result.catalog.forEach((resultData: IModelData) => {
                if (resultData.id && resultData.type === type) {
                    try {
                        this.getCreate(<I>resultData.id, resultData.store).setData(resultData);
                    } catch (err) {
                        this.logger.error(err);
                        // skip slently
                    }
                }
            });
        }
    }

    ///////////////////////////////////////////
    //
    // ASSETS
    //
    ///////////////////////////////////////////

    /**
     * Returns existing model (or models) or creates new one for given typeName and id
     * id is not needed only for singleton
     *
     * @param  {I} id
     * @param  {boolean} existingOnly
     * @return {Observable}
     */
    protected getModel(id: I): T;
    protected getModel(id: I, existingOnly: false, storeId?: number): T;
    protected getModel(id: I, existingOnly: true, storeId?: number): T | undefined;
    protected getModel(id: I, existingOnly: boolean = false, storeId?: number): T | undefined {
        const assets = this.getAssets(storeId);

        let asset: T | undefined = assets[<string>id];

        if (existingOnly) {
            if (!asset || !asset.data) {
                if (storeId === 0) {
                    for (const stId of Object.keys(this.externalAssets)) {
                        const m = this.externalAssets[stId][<string>id] as T;
                        if (m && m.data) {
                            return m;
                        }
                    }
                }
                return;
            }
        }
        if (!asset) {
            assets[id] = asset = this.createModelInstance(id);
            this.onCreateSubject.next(asset!);
        }
        return asset;
    }

    createModelInstance(id: I) {
        return new this.instance(id);
    }

    private getAssets(storeId?: number) {
        if (!storeId || storeId === this.multisiteService?.storeId) {
            return this.assets;
        }
        if (!this.externalAssets[storeId]) {
            this.externalAssets[storeId] = {};
        }
        return this.externalAssets[storeId];
    }

    /**
     * Gets model and if not exists creates new one
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {I} id
     * @return {T}
     */
    getCreate(id: I, storeId?: number): T {
        return <T>this.getModel(id, false, storeId);
    }

    /**
     * Gets model only if existss and has no error
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {I}      id
     * @return {T}
     */
    getExisting(id: I, storeId?: number): T | undefined {
        return this.getModel(id, true, storeId);
    }

    /**
     * Gets model - existing or not, but does not create it
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {I}      id
     * @return {T}
     */
    get(id: I): T {
        return this.getModel(id, false);
    }

    /**
     * Gets all models of given ids or all in general
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {I[]}    ids
     * @param  {boolean  = false}       existingOnly
     * @return {T[]}
     */
    getAll(ids?: I[], existingOnly: boolean = false, storeId: number = this.multisiteService?.storeId, includeVersions = false): T[] {
        if (ids) {
            return compact((<any>ids).map(id => (existingOnly ? this.getModel(id, true, storeId) : this.getModel(id, false, storeId))));
        }
        let objects = Object.values(this.getAssets(storeId));
        if (storeId) {
            objects = objects.filter(i => i.storeId === storeId);
        }
        if (!includeVersions) {
            return objects.filter(object => !object['isVersion']);
        }
        return objects;
    }

    /**
     * Gets all existing models that have no error
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {I[]}    ids
     * @return {T[]}
     */
    getAllExisting(ids?: I[], storeId: number = this.multisiteService?.storeId, includeVersions = false): T[] {
        return this.getAll(ids, true, storeId, includeVersions).filter(item => !!item.data);
    }

    /**
     * Deletes model and removes it from resource
     * @param {I} id
     */
    unload(id: I) {
        const model = this.assets[id];

        if (model) {
            delete this.assets[id];
            model.unload();
        }
    }

    setAsset(id: I, model: T | undefined) {
        const assets = this.getAssets(model?.storeId);
        model ? (assets[id] = model) : delete assets[id];
    }

    ///////////////////////////////////////////
    //
    // LOADING
    //
    ///////////////////////////////////////////

    /**
     * Provides observable response for model of given type and id
     * id is not needed only for singleton
     * @param  {string} typeName [description]
     * @param  {I} id [description]
     * @return {Observable} [description]
     */
    async load(id: I, verbosity: number, params?: Partial<TLoadParams>): Promise<T> {
        const model = this.getCreate(id);
        await model.load(verbosity, params);
        return model;
    }

    /**
     * Loads all given ids
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {I[]}    ids
     * @param  {number}       verbosity
     * @return {Promise<T[]>}
     */
    async loadAllIds(ids: I[], verbosity: number, params: Partial<TLoadParams> = {}): Promise<T[]> {
        const models = ids.map(id => this.getCreate(id));
        const promises = models.filter(model => model.isLoadNeeded(verbosity)).map(model => model.load(verbosity, params));
        if (promises.length) {
            await Promise.all(promises);
        }
        return models;
    }

    /**
     * Loads all models of resource
     */
    loadAll(verbosity: number = 1, params?: Partial<TLoadParams>): Promise<T[]> {
        return this.find({ filter: {}, verbosity }, params);
    }

    ///////////////////////////////////////////
    //
    // FINDING
    //
    ///////////////////////////////////////////

    find(findOptions: IFindRequestParams, params?: Partial<TLoadParams>): Promise<T[]> {
        const type = this.instance.type;

        return this.cacheService.cacheRequest(
            ['findModels', type, findOptions],
            () => this.findCallback(type, findOptions, params),
            undefined,
            undefined,
            {
                noCache: !!params?.noCache
            }
        );
    }

    protected async findCallback(type: string, findOptions: IFindRequestParams, params?: Partial<TLoadParams>): Promise<T[]> {
        const ids = await this.requestService.findModels<I>(Object.assign({ type }, findOptions));
        const models = this.toModels(ids, true, findOptions.filter['store']);
        const promises = models.map(model => model.load(findOptions.verbosity, { ...params, safe: true }));
        if (promises.length) {
            const loader = params?.loader;
            if (loader) {
                promises.forEach(i => loader.add(i));
            } else {
                const promise = Promise.all(promises);
                await promise;
            }
        }
        return models;
    }

    matchOne(filter: any): T | undefined {
        return this.getAllExisting().find(item => matches(filter)(item.data));
    }

    async findOneDirect(findOptions: IFindRequestParams, params?: Partial<TLoadParams>): Promise<T | undefined> {
        const model = await this.matchOne(findOptions.filter);
        if (model) {
            await model.load(findOptions.verbosity, params);
        }
        return model;
    }

    async findOne(findOptions: IFindRequestParams, params?: Partial<TLoadParams>): Promise<T | undefined> {
        const type = this.instance.type;
        try {
            const model = await this.findOneDirect(findOptions, params);
            if (model) {
                return model;
            }

            const resultData = await this.find({ ...findOptions, limit: 1 });
            const foundModel = resultData?.[0];

            if (!foundModel) {
                throw new Error(`Model not found (${JSON.stringify(findOptions.filter)})`);
            }
            return foundModel;
        } catch (err) {
            if (!params?.safe) {
                throw err;
            }
        }
    }

    loadMeta(loadOptions: IMetaCallRequest, params?: Partial<TLoadParams>): Promise<IMetaCallResponse> {
        return this.cacheService.cacheRequest(['resource.loadMeta', loadOptions], () =>
            this.requestService.sendXHR<IMetaCallResponse>(
                '/meta',
                { type: this.instance.type, ...loadOptions },
                { api: 'node', method: 'POST', ...(params || {}) }
            )
        );
    }

    ///////////////////////////////////////////
    //
    // INDEXES
    //
    ///////////////////////////////////////////

    /**
     * Indexes model
     * The idea is that we have indexes like
     * @param {Model}  model     [description]
     * @param {string} property [description]
     */
    index(model: T, property?: string) {
        if (model.data) {
            if (!property) {
                Object.keys(this.indexes).forEach(attrName => attrName && this.index(model, attrName));
            } else {
                try {
                    const value = this.getIndexValue(model, property);
                    const indexModel = this.getIndex(property);
                    const indexFun = v => {
                        if (typeof v === 'number' || typeof v === 'string') {
                            if (!indexModel[v]) {
                                indexModel[v] = {};
                            }
                            indexModel[v][<string>model.id] = true;
                        }
                    };
                    if (Array.isArray(value)) {
                        value.forEach(indexFun);
                    } else {
                        indexFun(value);
                    }
                } catch (err) {
                    console.error('Error', err, model);
                }
            }
        }
    }

    protected getIndexValue(model: T, property: string): number | number[] {
        const value: number | number[] = model.data?.[property];
        if (value) {
            const fixValue = (value: number) => {
                if (model?.type === 'product' && typeof value === 'string') {
                    const attr = this.app.resources.attribute.getExisting(property);
                    const optionValue = attr?.getOptionValue(value);
                    if (typeof optionValue === 'number') {
                        return optionValue;
                    }
                }
                return value;
            };

            if (Array.isArray(value)) {
                return value.map(fixValue);
            }
            return fixValue(value);
        }
        return value;
    }

    /**
     * Creates index for given type and addtribute code
     * @param {string} property Attribute code
     */
    createIndex(property: string): { [attributeValue: number | string]: { [modelId: number]: boolean } } {
        if (!this.indexes[property]) {
            this.indexes[property] = {};
            each(this.assets, (model: T) => this.index(model, property));
        }
        return this.indexes[property];
    }

    /**
     * Gets index for attribute
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {string} property
     * @return {any}
     */
    getIndex(property: string): IResourceIndex {
        return this.indexes[property] || this.createIndex(property);
    }

    ///////////////////////////////////////////
    //
    // UTIL
    //
    ///////////////////////////////////////////

    /**
     * Normalizes input to always get list of models
     * @param  {any} inputData
     * @return {model[]}
     */
    toModels(inputData: (T | I)[] | T | I, existingOnly: boolean = false, storeId?: number): T[] {
        return this.getAll(this.toIds(inputData), existingOnly, storeId);
    }

    /**
     * Normalizes input to always get signle model
     *
     * @param  {string} typeName
     * @param  {any} inputData
     * @return {model}
     */
    toModel(inputData: T | I): T;
    toModel(inputData: T | I, existingOnly: false, storeId?: number): T;
    toModel(inputData: T | I, existingOnly: boolean = false, storeId?: number): T | undefined {
        const id = this.toId(inputData);
        if (id) {
            return existingOnly === true ? this.getModel(id, true, storeId) : this.getModel(id, false, storeId);
        }
        return undefined;
    }

    /**
     * Normalizes input to always get single id
     * @param  {any} inputData
     * @return {number}
     */
    toId(inputData: T | I): I | undefined {
        if (typeof inputData === 'object') {
            return (<T>inputData).id;
        }
        return inputData;
    }

    /**
     * Normalizes input to always get list of numberic ids ids
     * @param  {any} inputData
     * @return {number[]}
     */
    toIds(inputData: (T | I)[] | T | I): I[] {
        return compact((Array.isArray(inputData) ? inputData : [inputData]).map(i => this.toId(i)));
    }
}
